import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import { dispatchSignUp } from 'reduxModules/auth/dispatchers'
import { registerBoxSelector } from 'reduxModules/auth/selectors'
import { validatePasswordField, isPasswordValid } from '../../../utils/passwordRules'
import PasswordStrength from '../PasswordStrength'
import { CaptchaNotice } from '../CaptchaNotice'
import keyUpHandler from '../../utils/keyUpHandler'
import FacebookSignIn from '../../generic/FacebookSignIn/index'
import OpenAthensSignIn from '../../generic/OpenAthensSignIn/index'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import '../RenderedField/styles.css'
import './styles.css'

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

export function RegisterBox({
  autoFocus = false,
  inCheckout = false,
  toggleAuth,
  isGlobalPromoEnabled,
  globalPromo,
  location,
  recaptcha,
  onSubmit
}) {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty }
  } = useForm()

  const password = watch('password1')

  // Gate submission on Turnstile: capture an early click and replay it once the
  // token lands, and keep the form locked until the submission settles.
  const { submit, awaitingVerification, isSubmitting } = useDeferredCaptchaSubmit(
    recaptcha.token,
    onSubmit
  )

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  const isSubmitDisabled =
    !isDirty || isSubmitting || awaitingVerification || !isPasswordValid(password)

  return (
    <div id="RegisterBox">
      <div className="sign-up-box auth-form-box">
        {inCheckout && (
          <button className="toggle-box" onClick={() => toggleAuth()}>
            Login
          </button>
        )}
        {inCheckout && (
          <div className="title">
            <div className="free-trial-text">
              {!isGlobalPromoEnabled && (
                <h3>Sign Up To Start Your Free 7-Day Trial</h3>
              )}
              {isGlobalPromoEnabled && (
                <h3>Subscribe and Save {globalPromo.discount}% with code: {globalPromo.codename}</h3>
              )}
            </div>
            <div className="create-account">
              Create your account
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit(submit)}>
          <div className={classnames('field', { '-error': errors.email })}>
            <div className="field-container">
              <input
                {...register('email', {
                  required: 'This field is required.',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Invalid email address.'
                  }
                })}
                type="email"
                placeholder="Email"
                aria-label="Email"
                autoFocus={autoFocus}
              />
              <span
                className={`field__error${!errors.email ? ' field__error--hidden' : ''}`}
                role="alert"
              >
                {errors.email?.message}
              </span>
            </div>
          </div>
          <div className={classnames('field', '-with-feedback', { '-error': errors.password1 })}>
            <div className="field-container">
              <input
                {...register('password1', {
                  required: 'This field is required.',
                  validate: validatePasswordField
                })}
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Password"
                aria-label="Password"
              />
              <i
                className={classnames('icon', passwordVisible ? 'icon-passcodewatch' : 'icon-passcodewatch disabled')}
                aria-label="Toggle password visibility"
                onClick={togglePasswordVisibility}
                onKeyUp={event => keyUpHandler(event, togglePasswordVisibility)}
                tabIndex="0"
              />
              <span
                className={`field__error${!errors.password1 ? ' field__error--hidden' : ''}`}
                role="alert"
              >
                {errors.password1?.message}
              </span>
            </div>
          </div>
          <PasswordStrength password={password} />
          <CaptchaStandalone />
          <p className="terms" aria-label="By clicking Register you are agreeing to the Terms and Conditions and have read the Warnings and Risks">
            By continuing you are agreeing to the
            <Link to="/customer-care/terms-of-use" target="_blank">Terms and Conditions</Link> and have read the <Link to="/customer-care/warnings-and-risks" target="_blank">Warnings and Risks</Link>.
          </p>
          {!inCheckout && (
            <button
              type="submit"
              className="btn -wide -blue-2"
              disabled={isSubmitDisabled}
              style={{ width: '100%' }}
            >
              {awaitingVerification ? 'Verifying…' : 'Register'}
            </button>
          )}
          {inCheckout && (
            <button
              type="submit"
              className="btn -wide -orange"
              disabled={isSubmitDisabled}
              style={{ width: '100%' }}
            >
              {awaitingVerification ? 'Verifying…' : 'Continue'}
            </button>
          )}
        </form>

        <div className="divisor">or</div>
        <FacebookSignIn
          text="Register with Facebook"
          location={location}
          inCheckout={inCheckout}
        />
        <OpenAthensSignIn />
        {!inCheckout && (
          <div className="auth-alt">
            or <Link to="/login">Sign In</Link> <br /> if you already have an account
          </div>
        )}
        <CaptchaNotice dark />
      </div>
    </div>
  )
}

RegisterBox.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  inCheckout: PropTypes.bool,
  toggleAuth: PropTypes.func,
  isGlobalPromoEnabled: PropTypes.bool,
  globalPromo: PropTypes.shape({
    discount: PropTypes.number,
    codename: PropTypes.string
  }),
  location: PropTypes.object,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  })
}

export default connect(
  registerBoxSelector,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const { dispatch } = dispatchProps
    return {
      ...stateProps,
      ...ownProps,
      onSubmit: (data) =>
        dispatchSignUp(
          dispatch,
          { ...data, recaptcha: stateProps.recaptcha },
          ownProps
        )
    }
  }
)(RegisterBox)
